// frontend/src/pages/decisoes/DecisoesListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function DecisoesListPage() {
  const { loading: authLoading, hasPermission } = useAuth();
  const canManage = hasPermission('config.decisoes');

  const [decisoes, setDecisoes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchDecisoes = async () => {
      setLoading(true);
      setError(null);

      if (!canManage) {
        setError({ kind: 'permission' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/decisoes', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        setDecisoes(response?.data?.data ?? []);
        setPaginationInfo(response?.data?.meta ?? null);
        setLoading(false);
      } catch (err) {
        if (controller.signal.aborted) return;
        setLoading(false);
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      }
    };

    fetchDecisoes();
    return () => controller.abort();
  }, [authLoading, canManage, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : decisoes.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza de que deseja desativar esta decisão?')) return;
    try {
      await api.delete(`/decisoes/${id}`);
      setDecisoes((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      if (err?.response?.status === 403) {
        alert('Acesso não permitido.');
      } else {
        alert('Ocorreu um erro ao desativar a decisão.');
      }
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Tem certeza de que deseja reativar esta decisão?')) return;
    try {
      await api.patch(`/decisoes/${id}/restore`);
      setDecisoes((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      if (err?.response?.status === 403) {
        alert('Acesso não permitido.');
      } else {
        alert('Ocorreu um erro ao reativar a decisão.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className="decisoes-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="decisoes-page">
        <LoadingSpinner text="Carregando decisões..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="decisoes-page">
          <PermissionDeniedAlert requiredPermission="config.decisoes" action="listar decisões" />
        </div>
      );
    }
    return (
      <div className="decisoes-page">
        <AlertException error={error} fallback="Não foi possível carregar as decisões." />
      </div>
    );
  }

  return (
    <div className="decisoes-page">
      <div className="content-header">
        <h1 className="content-title">Decisões</h1>
        <p className="content-subtitle">Lista de decisões cadastradas no sistema</p>
      </div>

      <div className="card decisoes-list-card">
        <div className="card-body">
          <div className="list-toolbar decisoes-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="decisoes-filters-group d-flex align-items-center gap-3">
              <div className="input-group decisoes-search-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="decisoes-status-filter d-flex align-items-center gap-2">
                <label htmlFor="decisoesStatusFilter" className="mb-0 decisoes-status-label">Filtrar por:</label>
                <select
                  id="decisoesStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar decisões por status"
                >
                  <option value="active">Ativas</option>
                  <option value="inactive">Inativas</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="decisoes-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            <Link to="/painel/decisoes/novo" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              Nova Decisão
            </Link>
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Data de Criação</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {decisoes.length > 0 ? (
                  decisoes.map((decisao) => {
                    const isInactive = Boolean(decisao.deleted_at);

                    return (
                      <tr key={decisao.id}>
                        <td data-label="Nome">{decisao.nome}</td>
                        <td data-label="Data de Criação">{decisao.created_at ? String(decisao.created_at).split(' ')[0] : '-'}</td>
                        <td data-label="Status">{isInactive ? 'Inativa' : 'Ativa'}</td>
                        <td className="table-actions decisoes-table-actions d-flex gap-2" data-label="Ações">
                          {!isInactive && (
                            <Link
                              to={`/painel/decisoes/${decisao.id}/editar`}
                              className="btn btn-sm btn-outline-warning"
                              title="Editar"
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}

                          {!isInactive && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Desativar"
                              onClick={() => handleDelete(decisao.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}

                          {isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(decisao.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">Nenhuma decisão encontrada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="decisoes-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> -{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DecisoesListPage;
